<script>
import { ref, onMounted, onBeforeUnmount } from '@vue/composition-api'
import adminApi from '@/api/admin';
import { i18n } from "@/plugins/i18n";

export default {
    props: ["screen"],
    setup(props) {
        const fileInput = ref(null);
        const currentImage = ref('');
        const selectedFile = ref(null);
        const previewSrc = ref('');
        const isLoading = ref(false);
        const isSubmitting = ref(false);
        const errorMessage = ref('');

        const fetchLogo = async () => {
            isLoading.value = true;
            const response = await adminApi.getUser(props.screen);
            if (response.ok && response.data.picture) {
                currentImage.value = response.data.picture.full_hd || '';
            }
            isLoading.value = false;
        }

        const pickFile = () => {
            errorMessage.value = '';
            fileInput.value && fileInput.value.click();
        }

        const onFileChange = (event) => {
            const file = event.target.files && event.target.files[0];
            event.target.value = '';
            if (!file) return;

            errorMessage.value = '';
            if (previewSrc.value) URL.revokeObjectURL(previewSrc.value);
            previewSrc.value = URL.createObjectURL(file);
            selectedFile.value = file;
        }

        const saveLogo = async () => {
            if (!selectedFile.value) return;
            isSubmitting.value = true;
            errorMessage.value = '';

            const response = await adminApi.updateUserImage(props.screen, selectedFile.value, selectedFile.value.name);
            if (response.ok) {
                if (previewSrc.value) {
                    URL.revokeObjectURL(previewSrc.value);
                    previewSrc.value = '';
                }
                selectedFile.value = null;
                await fetchLogo();
            } else {
                errorMessage.value = i18n.t('Logon tallennus epäonnistui.');
            }
            isSubmitting.value = false;
        }

        const removeLogo = async () => {
            isSubmitting.value = true;
            errorMessage.value = '';

            const response = await adminApi.removeUserImage(props.screen);
            if (response.ok) {
                currentImage.value = '';
                if (previewSrc.value) {
                    URL.revokeObjectURL(previewSrc.value);
                    previewSrc.value = '';
                }
                selectedFile.value = null;
            } else {
                errorMessage.value = i18n.t('Logon poistaminen epäonnistui.');
            }
            isSubmitting.value = false;
        }

        onMounted(() => {
            fetchLogo();
        })

        onBeforeUnmount(() => {
            if (previewSrc.value) URL.revokeObjectURL(previewSrc.value);
        })

        return {
            fileInput,
            currentImage,
            selectedFile,
            previewSrc,
            isLoading,
            isSubmitting,
            errorMessage,
            pickFile,
            onFileChange,
            saveLogo,
            removeLogo,
        }
    }
}
</script>
<template>
    <div>
        <v-alert color="info" text>
            {{ $t('Näytön logo korvaa yrityksen logon tällä näytöllä. Kuvan ei tarvitse olla neliö.') }}
        </v-alert>

        <div class="logo-preview-wrapper" style="max-width: 400px;">
            <div class="checkered-bg">
                <img v-if="previewSrc || currentImage" :src="previewSrc || currentImage" class="logo-preview" />
                <div v-else class="empty-logo text--secondary">
                    {{ $t('Ei logoa') }}
                </div>
            </div>
        </div>

        <div class="mt-4">
            <input ref="fileInput" type="file" accept=".jpeg,.jpg,.png" class="d-none" @change="onFileChange" />
            <v-btn outlined class="mr-2" @click="pickFile" :disabled="isSubmitting">
                {{ $t("Valitse kuva") }}
            </v-btn>
            <v-btn color="primary" class="mr-2" @click="saveLogo" :loading="isSubmitting" :disabled="!selectedFile">
                {{ $t("Tallenna") }}
            </v-btn>
            <v-btn text color="error" @click="removeLogo" :disabled="isSubmitting || (!currentImage && !selectedFile)">
                {{ $t("Poista logo") }}
            </v-btn>
        </div>

        <v-alert v-if="errorMessage" type="error" text class="mt-4" dismissible @input="errorMessage = ''">
            {{ errorMessage }}
        </v-alert>
    </div>
</template>

<style scoped>
.checkered-bg {
    background-color: #fff;
    background-image:
        linear-gradient(45deg, #e0e0e0 25%, transparent 25%),
        linear-gradient(-45deg, #e0e0e0 25%, transparent 25%),
        linear-gradient(45deg, transparent 75%, #e0e0e0 75%),
        linear-gradient(-45deg, transparent 75%, #e0e0e0 75%);
    background-size: 16px 16px;
    background-position: 0 0, 0 8px, 8px -8px, -8px 0;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 4px;
    padding: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 160px;
}

.logo-preview {
    max-width: 100%;
    max-height: 300px;
    object-fit: contain;
}

.empty-logo {
    font-style: italic;
}
</style>
